import React, { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  Clock3,
  MonitorUp,
  RefreshCw,
  ShieldCheck,
  Users,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuth } from '@/core/hooks';

type Period = 'day' | 'week' | 'month';

interface UsageBucket {
  bucket: string;
  minutes: number;
  hours: number;
  sessions: number;
  users: number;
}

interface LiveUser {
  sessionId: string;
  userId: string;
  name: string;
  email: string;
  roles: string[];
  startedAt: string;
  lastActivityAt: string;
  currentPath: string;
  pageTitle: string;
  ip: string;
  device: string;
}

interface UserUsage {
  userId: string;
  name: string;
  email: string;
  roles: string[];
  totalMinutes: number;
  totalHours: number;
  sessions: number;
  lastActivityAt: string;
  currentPath: string;
}

interface OwnerActivitySnapshot {
  generatedAt: string;
  summary: {
    usersTotal: number;
    liveUsers: number;
    liveSessions: number;
    sessionsStored: number;
    totalUsageMinutes: number;
  };
  liveUsers: LiveUser[];
  usage: Record<Period, UsageBucket[]>;
  users: UserUsage[];
}

const emptySnapshot: OwnerActivitySnapshot = {
  generatedAt: '',
  summary: {
    usersTotal: 0,
    liveUsers: 0,
    liveSessions: 0,
    sessionsStored: 0,
    totalUsageMinutes: 0,
  },
  liveUsers: [],
  usage: { day: [], week: [], month: [] },
  users: [],
};

function authHeaders(token?: string | null): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatDateTime(value?: string) {
  if (!value) return 'No activity yet';
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(minutes: number) {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} days`;
}

function shortPath(path?: string) {
  if (!path) return 'Application open';
  return path.length > 38 ? `${path.slice(0, 35)}...` : path;
}

export const OwnerActivityMonitor: React.FC = () => {
  const { token } = useAuth();
  const [snapshot, setSnapshot] = useState<OwnerActivitySnapshot>(emptySnapshot);
  const [period, setPeriod] = useState<Period>('day');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    if (!token) return;
    setError('');
    try {
      const params = new URLSearchParams({
        page: window.location.pathname,
        title: document.title,
      });
      const response = await fetch(`/api/auth/owner-activity?${params.toString()}`, {
        headers: authHeaders(token),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not load owner activity');
      setSnapshot(payload.data || emptySnapshot);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Could not load owner activity');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(timer);
  }, [token]);

  const usageData = snapshot.usage[period] || [];
  const topUsers = useMemo(() => snapshot.users.slice(0, 12), [snapshot.users]);
  const liveUserIds = new Set(snapshot.liveUsers.map((item) => item.userId));

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-3 text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                <ShieldCheck className="h-5 w-5" />
                Owner live control
              </div>
              <h1 className="mt-2 text-3xl font-bold text-slate-950">Application Usage Monitor</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Live visibility for connected users, active sessions and usage time by user across days, weeks and months.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void load()}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-800">
            {error}
          </div>
        ) : null}

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard label="Live users" value={snapshot.summary.liveUsers} detail={`${snapshot.summary.liveSessions} active sessions`} icon={<Users className="h-5 w-5" />} />
          <MetricCard label="Known users" value={snapshot.summary.usersTotal} detail="user accounts tracked" icon={<MonitorUp className="h-5 w-5" />} />
          <MetricCard label="Stored sessions" value={snapshot.summary.sessionsStored} detail="session history available" icon={<Activity className="h-5 w-5" />} />
          <MetricCard label="Total usage" value={formatDuration(snapshot.summary.totalUsageMinutes)} detail="from saved sessions" icon={<Clock3 className="h-5 w-5" />} />
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-950">Usage trend</h2>
                <p className="mt-1 text-sm text-slate-600">Total application time and active users for the selected period.</p>
              </div>
              <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-1">
                {(['day', 'week', 'month'] as Period[]).map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setPeriod(item)}
                    className={`rounded px-3 py-1.5 text-sm font-semibold capitalize ${period === item ? 'bg-[#6c3a80] text-white' : 'text-slate-600 hover:bg-white'}`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-5 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={usageData}>
                  <defs>
                    <linearGradient id="usageHours" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6c3a80" stopOpacity={0.34} />
                      <stop offset="95%" stopColor="#6c3a80" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 12 }} stroke="#64748b" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#64748b" />
                  <Tooltip formatter={(value, name) => [name === 'hours' ? `${Number(value).toFixed(2)} h` : value, name === 'hours' ? 'Usage time' : 'Active users']} />
                  <Area type="monotone" dataKey="hours" stroke="#6c3a80" strokeWidth={3} fill="url(#usageHours)" />
                  <Area type="monotone" dataKey="users" stroke="#0f766e" strokeWidth={2} fill="#ccfbf1" fillOpacity={0.32} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-bold text-slate-950">Usage by user</h2>
            <p className="mt-1 text-sm text-slate-600">Total time calculated from saved session activity.</p>
            <div className="mt-5 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topUsers} layout="vertical" margin={{ left: 12, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="#64748b" />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} stroke="#64748b" />
                  <Tooltip formatter={(value) => [`${Number(value).toFixed(2)} h`, 'Usage time']} />
                  <Bar dataKey="totalHours" fill="#6c3a80" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-950">Connected now</h2>
              <p className="mt-1 text-sm text-slate-600">Users with server activity in the last 3 minutes.</p>
            </div>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-bold text-emerald-800">
              {snapshot.liveUsers.length} live
            </span>
          </div>
          <div className="mt-5 grid gap-3 lg:grid-cols-2">
            {snapshot.liveUsers.map((liveUser) => (
              <div key={liveUser.sessionId} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-slate-950">{liveUser.name}</p>
                    <p className="text-sm text-slate-600">{liveUser.email}</p>
                  </div>
                  <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-800">Online</span>
                </div>
                <div className="mt-3 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
                  <p><span className="font-semibold">Role:</span> {liveUser.roles.join(', ') || 'User'}</p>
                  <p><span className="font-semibold">Last seen:</span> {formatDateTime(liveUser.lastActivityAt)}</p>
                  <p><span className="font-semibold">Page:</span> {shortPath(liveUser.currentPath)}</p>
                  <p><span className="font-semibold">IP:</span> {liveUser.ip || 'Unknown'}</p>
                </div>
              </div>
            ))}
            {!snapshot.liveUsers.length ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-5 text-sm text-slate-500">
                No other active sessions detected in the last 3 minutes.
              </div>
            ) : null}
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-xl font-bold text-slate-950">User usage detail</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead>
                <tr className="text-left text-slate-600">
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Role</th>
                  <th className="py-2 pr-4">Total time</th>
                  <th className="py-2 pr-4">Sessions</th>
                  <th className="py-2 pr-4">Last activity</th>
                  <th className="py-2 pr-4">Last page</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {snapshot.users.map((usageUser) => (
                  <tr key={usageUser.userId}>
                    <td className="py-3 pr-4">
                      <p className="font-semibold text-slate-950">{usageUser.name}</p>
                      <p className="text-xs text-slate-500">{usageUser.email}</p>
                    </td>
                    <td className="py-3 pr-4 text-slate-700">{usageUser.roles.join(', ') || 'User'}</td>
                    <td className="py-3 pr-4 font-semibold text-[#6c3a80]">{formatDuration(usageUser.totalMinutes)}</td>
                    <td className="py-3 pr-4 text-slate-700">{usageUser.sessions}</td>
                    <td className="py-3 pr-4 text-slate-700">{formatDateTime(usageUser.lastActivityAt)}</td>
                    <td className="py-3 pr-4 text-slate-700">{shortPath(usageUser.currentPath)}</td>
                    <td className="py-3 pr-4">
                      <span className={`rounded-full px-2 py-1 text-xs font-bold ${liveUserIds.has(usageUser.userId) ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>
                        {liveUserIds.has(usageUser.userId) ? 'Online' : 'Offline'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

const MetricCard: React.FC<{ label: string; value: React.ReactNode; detail: string; icon: React.ReactNode }> = ({ label, value, detail, icon }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-medium text-slate-500">{label}</p>
        <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
        <p className="mt-1 text-sm text-slate-600">{detail}</p>
      </div>
      <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">{icon}</div>
    </div>
  </div>
);
